import { useFullRelease } from "@/common/hooks_useFullRelease";
import NoRecords from "@/pages/NoRecords";
import styled from "@emotion/styled";
import SplitPane from "@imminently/decisively-ui/SplitPane";
import TemporalSliceProvider from "@imminently/decisively-ui/TemporalSliceProvider";
import type React from "react";
import { useEffect, useState } from "react";
import AttributeInfo from "./components/AttributeInfo/AttributeInfo";
import Classic from "./components/Classic";
import Data from "./components/Data/Data";
import GraphToolbar from "./components/GraphToolbar/GraphToolbar";
import Nebula from "./components/Nebula";
import Report from "./components/Report";
import { useGraphVisualisation } from "./hooks/useGraphVisualisation";
import RuleGraphProvider from "./providers/RuleGraphProvider";
import type { DecisionReportDownloadContext } from "./providers/RuleGraphProvider";
import type { RuleGraphVizBaseProps } from "./types/RuleGraphViz";

const Container = styled.div`
  /* display: flex; */
  /* flex-flow: column; */
  /* flex: 1; */
  height: 100%;
  background-color: var(--background);
  overflow: hidden !important;
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const VisualisationArea = styled(SplitPane)`
`;

export interface GraphVizTypeInfo {
  id: string;
  nameTranslationKey: string;
  component: React.ComponentType<any>;
  /** Rendered in the toolbar in place of the default graph controls (search/depth/stats/focus) while this viz type is active. */
  toolbarComponent?: React.ComponentType<any>;
}

// import stl from "./GraphViz.module.scss";

// TODO we'll see what from here I move into RuleGraphVizBaseProps
interface GraphVizProps {
  /** Show/hide the graph toolbar. Default - true */
  showToolbar?: boolean;
  graph: any;
  debug?: boolean;
  hasClassic?: boolean;
  hasNebula?: boolean;
  hasDecisionReport?: boolean;
  hasData?: boolean;
  hasFocus?: boolean;
  report?: any;
  goal?: any;
  decisionReportDownloadContext?: DecisionReportDownloadContext | null;
  extraVizTypes?: GraphVizTypeInfo[];
  children?: React.ReactNode;
  /** Fires whenever the selected node changes, so callers can lift selection state outside this component's subtree. */
  onSelectedNodeChange?: (node: any) => void;
  /** Bump this (e.g. a counter) to clear the current selection from outside this component's subtree. */
  clearSelectedNodeSignal?: number;
}

export type WithGraphCoreSplitProps = React.PropsWithChildren<{
  className?: string;
}>;
/**
 * when we work with sessions at "/monitor/overview" route, we need to\
 * be able to show Data tab outside of GraphCore, but also have the\
 * right sidebar opened when we select an attribute. And in order to do\
 * that the best option seems to extract only the part with SplitPane\
 * into a separate component and reuse it both in GraphCore and then in\
 * "/monitor/overview". \
 * This component is the result of such extraction.
 */
export const WithGraphCoreSplit: React.FC<WithGraphCoreSplitProps> = (p) => {
  const { children, className } = p;
  const { selectedNode } = useGraphVisualisation();

  return (
    <VisualisationArea
      className={className}
      direction="horizontal"
      gutterSize={6}
    >
      <SplitPane.Pane
        minSize={300}
        preferredSize={"70%"}
      >
        {children}
      </SplitPane.Pane>
      {selectedNode ? (
        <SplitPane.Pane
          preferredSize={320}
          minSize={320}
        >
          <AttributeInfo />
        </SplitPane.Pane>
      ) : null}
    </VisualisationArea>
  );
};
export const GraphCore = () => {
  const { graphVizType, extraVizTypes, temporalSlice } = useGraphVisualisation();

  const renderViz = (report) => {
    switch (report) {
      case "classic":
        return <Classic />;
      case "classic-beta":
        return <Classic />;
      case "nebula":
        return <Nebula />;
      case "data":
        return (
          <Data
            session={undefined}
            onGoToInputAndSelectInteraction={undefined}
          />
        );
      case "decisionreport":
        return <Report />;
      default: {
        const extraVizType = extraVizTypes?.find((vizType) => vizType.id === report);
        if (extraVizType) {
          const { component: VizComponent } = extraVizType;
          return <VizComponent />;
        }

        return (
          <div>
            <br />
            <br />
            Unknown visualization type: <strong>{report}</strong>
          </div>
        );
      }
    }
  };

  return (
    <TemporalSliceProvider value={temporalSlice}>
      <Container>
        <GraphToolbar />
        <WithGraphCoreSplit>{renderViz(graphVizType)}</WithGraphCoreSplit>
      </Container>
    </TemporalSliceProvider>
  );
};

const GraphViz = (props: GraphVizProps & RuleGraphVizBaseProps) => {
  const {
    showToolbar = true,
    children,
    graph,
    debug = false,
    hasClassic,
    hasNebula,
    hasDecisionReport = false,
    hasData,
    report,
    goal,
    decisionReportDownloadContext,
    extraVizTypes,
    onSelectedNodeChange,
    clearSelectedNodeSignal,
  } = props;
  const release = useFullRelease();
  if (!release) return null; // There is a period when the release is loading, so don't showing anything

  if (!graph) {
    return (
      <NoRecords
        title="No Graph Detected"
        body="Either you haven't started modelling, or an error has occurred. If the latter, please contact support."
      />
    );
  }

  return (
    <RuleGraphProvider
      goal={goal}
      report={report}
      debug={debug}
      graph={graph}
      hasNebula={hasNebula}
      hasData={hasData}
      hasClassic={hasClassic}
      hasDecisionReport={hasDecisionReport}
      decisionReportDownloadContext={decisionReportDownloadContext}
      extraVizTypes={extraVizTypes}
      onSelectedNodeChange={onSelectedNodeChange}
      clearSelectedNodeSignal={clearSelectedNodeSignal}
    >
      <GraphCore />
      {children}
    </RuleGraphProvider>
  );
};

export default GraphViz;
